import React, { useEffect, useState, useCallback, useRef } from "react";
import { toast } from "sonner";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { CATEGORY_META, CATEGORY_ORDER, STATUS_META, PRIORITY_META } from "@/lib/constants";
import { KanbanBoard } from "@/components/views/KanbanBoard";
import { CalendarView } from "@/components/views/CalendarView";
import TaskDialog from "@/components/TaskDialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { LayoutGrid, Calendar as CalendarIcon, Bookmark, Plus, Trash2, Globe, Lock } from "lucide-react";

const VIEWS = [
  { key: "board", label: "Board", icon: LayoutGrid },
  { key: "calendar", label: "Calendar", icon: CalendarIcon },
];

export default function StudioBoard() {
  const { user } = useAuth();
  const [view, setView] = useState("board");
  const [category, setCategory] = useState("all");
  const [assignee, setAssignee] = useState("all");
  const [priority, setPriority] = useState("all");
  const [tasks, setTasks] = useState([]);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogTask, setDialogTask] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [views, setViews] = useState([]);

  const canManage = user?.role === "admin" || user?.can_manage;

  const loadViews = useCallback(() => api.get("/views", { params: { scope: "board" } }).then((r) => setViews(r.data)).catch(() => {}), []);
  useEffect(() => { loadViews(); }, [loadViews]);

  const saveView = async () => {
    const name = window.prompt("Name this board view (e.g. Interior · High priority):");
    if (!name || !name.trim()) return;
    try {
      await api.post("/views", { name: name.trim(), scope: "board", filters: { category, assignee, priority } });
      loadViews();
      toast.success("Board view saved");
    } catch (e) { toast.error(apiErr(e, "Couldn't save this view")); }
  };
  const applyView = (v) => {
    const f = v.filters || {};
    setCategory(f.category || "all");
    setAssignee(f.assignee || "all");
    setPriority(f.priority || "all");
  };
  const deleteView = async (id) => {
    try { await api.delete(`/views/${id}`); loadViews(); } catch { toast.error("Couldn't delete this view"); }
  };
  const toggleShare = async (v) => {
    try {
      await api.patch(`/views/${v.view_id}`, { shared: !v.shared });
      loadViews();
      toast.success(v.shared ? "View is now private" : "Shared with the team");
    } catch { toast.error("Couldn't update sharing"); }
  };

  const loadedOnce = useRef(false);
  const load = useCallback(async () => {
    // Only show the full-page spinner on first load; refetches (e.g. after a
    // quick-assign) must NOT unmount KanbanBoard, or its openAssignId state
    // (which keeps the popover open for multi-pick) would be lost.
    if (!loadedOnce.current) setLoading(true);
    try {
      const query = {};
      if (category !== "all") query.category = category;
      const [t, u] = await Promise.all([
        api.get("/tasks", { params: query }),
        api.get("/users"),
      ]);
      setTasks(t.data);
      setMembers(u.data);
      loadedOnce.current = true;
    } catch (e) {
      toast.error(apiErr(e, "Could not load the board"));
    } finally {
      setLoading(false);
    }
  }, [category]);

  useEffect(() => { load(); }, [load]);

  const memberMap = Object.fromEntries(members.map((m) => [m.user_id, m]));
  const filteredTasks = tasks.filter((t) => {
    const asg = t.assigned_to || [];
    const assigneeOk = assignee === "all"
      || (assignee === "__none" && asg.length === 0)
      || asg.includes(assignee);
    const priorityOk = priority === "all" || t.priority === priority;
    return assigneeOk && priorityOk;
  });
  const openTask = (t) => { setDialogTask(t); setDialogOpen(true); };

  const changeStatus = async (id, status) => {
    setTasks((prev) => prev.map((t) => (t.task_id === id ? { ...t, status } : t)));
    try {
      const res = await api.patch(`/tasks/${id}`, { status });
      // Backend may auto-flip not_assigned⇄assigned based on assignees — reconcile
      // local state with the server's resolved status so the card sits in the right column.
      const resolved = res?.data?.status;
      if (resolved && resolved !== status) {
        setTasks((prev) => prev.map((t) => (t.task_id === id ? { ...t, status: resolved } : t)));
      }
      toast.success(`Moved to ${STATUS_META[resolved || status]?.label || resolved || status}`);
    } catch (e) {
      toast.error(apiErr(e, "Could not move task"));
      load();
    }
  };

  return (
    <div className="p-6 lg:p-10 max-w-[1600px]">
      <header className="mb-6">
        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-zinc-500 mb-1">Whole Studio</p>
        <h1 className="font-heading text-4xl font-black tracking-tighter">Studio Board</h1>
        <p className="text-zinc-500 mt-2 text-sm">Every task across every project in one board or calendar.</p>
      </header>

      <div className="flex flex-wrap items-center gap-3 mb-6">
        <div className="inline-flex rounded-sm border border-zinc-200 bg-white p-0.5" data-testid="studio-board-view-switch">
          {VIEWS.map((v) => {
            const Icon = v.icon;
            const active = view === v.key;
            return (
              <button key={v.key} onClick={() => setView(v.key)}
                className={`inline-flex items-center gap-1.5 px-3.5 py-1.5 text-sm font-medium rounded-sm transition-colors ${active ? "bg-zinc-900 text-white" : "text-zinc-600 hover:bg-zinc-100"}`}
                data-testid={`studio-view-${v.key}`}>
                <Icon className="h-4 w-4" />
                {v.label}
              </button>
            );
          })}
        </div>

        <Select value={category} onValueChange={setCategory}>
          <SelectTrigger className="w-[210px] rounded-sm" data-testid="studio-board-category"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All folders</SelectItem>
            {CATEGORY_ORDER.map((c) => <SelectItem key={c} value={c}>{CATEGORY_META[c].label} · {CATEGORY_META[c].sub}</SelectItem>)}
          </SelectContent>
        </Select>

        <Select value={assignee} onValueChange={setAssignee}>
          <SelectTrigger className="w-[170px] rounded-sm" data-testid="studio-board-assignee"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All assignees</SelectItem>
            <SelectItem value="__none">Unassigned</SelectItem>
            {members.map((m) => <SelectItem key={m.user_id} value={m.user_id}>{m.name}</SelectItem>)}
          </SelectContent>
        </Select>

        <Select value={priority} onValueChange={setPriority}>
          <SelectTrigger className="w-[150px] rounded-sm" data-testid="studio-board-priority"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All priorities</SelectItem>
            {Object.keys(PRIORITY_META).map((p) => <SelectItem key={p} value={p}>{PRIORITY_META[p].label || p}</SelectItem>)}
          </SelectContent>
        </Select>

        {(category !== "all" || assignee !== "all" || priority !== "all") && (
          <>
            <button
              data-testid="studio-board-reset-filters"
              onClick={() => { setCategory("all"); setAssignee("all"); setPriority("all"); }}
              className="text-xs px-3 py-1.5 rounded-full border border-zinc-300 text-zinc-600 hover:bg-zinc-100 transition-colors"
            >
              Reset filters
            </button>
            <button
              data-testid="studio-board-save-view"
              onClick={saveView}
              className="text-xs font-semibold text-zinc-900 hover:underline inline-flex items-center gap-1"
            >
              <Plus className="h-3.5 w-3.5" /> Save view
            </button>
          </>
        )}

        <span className="text-xs text-zinc-400 ml-auto">{loading ? "Loading…" : `${filteredTasks.length} task${filteredTasks.length !== 1 ? "s" : ""}`}</span>
      </div>

      {views.length > 0 && (
        <div className="flex flex-wrap items-center gap-2 mb-6 -mt-2" data-testid="studio-board-saved-views">
          <span className="text-xs font-semibold uppercase tracking-wider text-zinc-400 flex items-center gap-1"><Bookmark className="h-3.5 w-3.5" /> Saved:</span>
          {views.map((v) => (
            <span key={v.view_id} className="group inline-flex items-center gap-1 rounded-full bg-zinc-100 hover:bg-zinc-200 transition-colors pl-3 pr-1 py-1" data-testid={`studio-saved-view-${v.view_id}`}>
              <button onClick={() => applyView(v)} className="text-xs font-medium text-zinc-800 flex items-center gap-1">
                {v.shared && <Globe className="h-3 w-3 text-emerald-600" />}
                {v.name}
                {!v.is_mine && <span className="text-[10px] text-zinc-400">· {v.owner_name}</span>}
              </button>
              {v.is_mine && (
                <button onClick={() => toggleShare(v)} title={v.shared ? "Make private" : "Share with team"}
                  className={`p-0.5 ${v.shared ? "text-emerald-600" : "text-zinc-400 hover:text-emerald-600"}`}
                  data-testid={`studio-share-view-${v.view_id}`}>
                  {v.shared ? <Globe className="h-3 w-3" /> : <Lock className="h-3 w-3" />}
                </button>
              )}
              {(v.is_mine || user?.role === "admin") && (
                <button onClick={() => deleteView(v.view_id)} className="text-zinc-400 hover:text-red-600 p-0.5" data-testid={`studio-delete-view-${v.view_id}`}><Trash2 className="h-3 w-3" /></button>
              )}
            </span>
          ))}
        </div>
      )}

      {members.length > 0 && (
        <div className="flex flex-wrap items-center gap-2 mb-6 -mt-2" data-testid="studio-board-avatar-row">
          <button onClick={() => setAssignee("all")}
            className={`text-xs px-3 py-1 rounded-full border transition-colors ${assignee === "all" ? "bg-zinc-900 text-white border-zinc-900" : "border-zinc-300 text-zinc-600 hover:bg-zinc-100"}`}>
            Everyone
          </button>
          {members.map((m) => {
            const active = assignee === m.user_id;
            const initials = (m.name || "?").split(" ").map((x) => x[0]).slice(0, 2).join("").toUpperCase();
            return (
              <button key={m.user_id} title={m.name}
                onClick={() => setAssignee(active ? "all" : m.user_id)}
                data-testid={`studio-avatar-${m.user_id}`}
                className={`h-8 w-8 rounded-full flex items-center justify-center text-[11px] font-semibold ring-2 transition-all overflow-hidden ${active ? "ring-zinc-900" : "ring-transparent hover:ring-zinc-300"}`}
                style={{ background: active ? "#18181b" : "#e4e4e7", color: active ? "#fff" : "#3f3f46" }}>
                {m.picture ? <img src={m.picture} alt={m.name} className="h-8 w-8 rounded-full object-cover" /> : initials}
              </button>
            );
          })}
        </div>
      )}

      {loading ? (
        <div className="p-10"><div className="h-8 w-8 border-2 border-zinc-900 border-t-transparent rounded-full animate-spin" /></div>
      ) : view === "board" ? (
        <KanbanBoard tasks={filteredTasks} memberMap={memberMap} members={members} onOpenTask={openTask} onStatusChange={changeStatus} canManage={canManage} onSaved={load} excludeStatuses={["not_assigned"]} />
      ) : (
        <CalendarView tasks={filteredTasks} onOpenTask={openTask} />
      )}

      <TaskDialog
        open={dialogOpen} onOpenChange={setDialogOpen}
        task={dialogTask} projectId={dialogTask?.project_id}
        members={members} canManage={canManage} currentUser={user}
        onSaved={load}
      />
    </div>
  );
}
